<template>
  <div class="sidebar fade-in">
    <div class="oa-filters">
      <input
        v-model="oaFilter"
        placeholder="Filter OA Number"
        class="oa-filter-input"
        type="text"
      />
      <input
        v-model="customerFilter"
        placeholder="Filter Customer Name"
        class="oa-filter-input"
        type="text"
      />
      <input
        v-model="statusFilter"
        placeholder="Filter Status"
        class="oa-filter-input"
        type="text"
      />
    </div>
    <div class="sidebar-table-container">
    <table class="sidebar-table">
      <thead>
        <tr>
          <th>OA Number</th>
          <th>Customer Name</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in filteredOAList" :key="idx">
          <td>{{ row.oa }}</td>
          <td>{{ row.customer }}</td>
          <td>{{ row.status }}</td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>

  <div class="body fade-in">
    <h2>OA Registrar</h2>
    <table class="bom-table" v-if="oaRows.length">
      <thead>
        <tr>
          <th>OA Date</th>
          <th>Particulars</th>
          <th>Order Qty</th>
          <th>OA Number</th>
          <th>Pending Qty</th>
          <th>FG Qty</th>
          <th>FG Status</th>
          <th>Component Status</th>
          <th>FG Movement</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="({ row, group, isFirstInGroup, isLastInGroup }, idx) in groupedOARows"
          :key="idx"
          :class="getRowClass(row, group, isFirstInGroup, isLastInGroup)"
        >
          <td>{{ row[0] }}</td>
          <td>{{ row[1] }}</td>
          <td>{{ row[2] }}</td>
          <td>{{ row[3] }}</td>
          <td>{{ row[4] }}</td>
          <td>{{ row[6] }}</td>
          <td>{{ row[7] }}</td>
          <td>{{ row[8] }}</td>
          <td>{{ row[11] }}</td>
        </tr>
      </tbody>
    </table>
    <div v-else class="no-data fade-in">No data found.</div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';


const backendUrl = process.env.VUE_APP_BACKEND_URL;
const FILTERS_KEY = 'oaRegistrarFilters';

export default {
  name: "OARegistrar",
  setup() {
    const oaRows = ref([]);
    const oaList = ref([]);
    const oaFilter = ref('');
    const customerFilter = ref('');
    const statusFilter = ref('ACTIVE');
    const fetchOARegistrar = async () => {
      const response = await fetch(`${backendUrl}/excel/oa-registrar`);
      const data = await response.json();
      oaRows.value = data.items || [];
    };
    // Fetch unique OA/Customer pairs for the sidebar table
    const fetchUniqueOAs = async () => {
      const response = await fetch(`${backendUrl}/excel/oa-registrar/unique-oas`);
      const data = await response.json();
      oaList.value = data.items || [];
    };
    const filteredOAList = computed(() =>
      oaList.value.filter(row =>
        (!oaFilter.value || String(row.oa).toLowerCase().includes(oaFilter.value.toLowerCase())) &&
        (!customerFilter.value || String(row.customer).toLowerCase().includes(customerFilter.value.toLowerCase())) &&
        (!statusFilter.value || String(row.status).toLowerCase().includes(statusFilter.value.toLowerCase()))
      )
    );
    const filteredOANumbers = computed(() =>
      filteredOAList.value.map(row => row.oa)
    );
    const filteredOARows = computed(() =>
      oaRows.value.filter(row =>
        filteredOANumbers.value.includes(row[9]) // if row 9 is the OA number in the body table
      )
    );
    const groupedOARows = computed(() => {
      let group = -1;
      let prevGroup = -1;
      return filteredOARows.value.map((row, idx, arr) => {
        if (row[0] && !isNaN(Date.parse(row[0]))) {
          group += 1;
        }
        // Determine if this is the first or last row in the group
        const isFirstInGroup = (idx === 0) || (group !== prevGroup);
        const isLastInGroup =
          idx === arr.length - 1 ||
          (arr[idx + 1][0] && !isNaN(Date.parse(arr[idx + 1][0])));
        prevGroup = group;
        return { row, group, isFirstInGroup, isLastInGroup };
      });
    });

    function getRowClass(row, group, isFirstInGroup, isLastInGroup) {
      let classes = [];
      const isDateRow = row[0] && !isNaN(Date.parse(row[0]));
      if (typeof group !== 'undefined') {
        classes.push(`row-group-${group % 2}`);
        classes.push('row-group-box');
        if (isDateRow || isFirstInGroup) classes.push('row-group-top');
        if (isLastInGroup) classes.push('row-group-bottom');
      }
      if (isDateRow) {
        classes.push('date-row');
      } else {
        // Only apply conditional coloring to non-date rows
        if (row[7] && row[7].toLowerCase() === 'order complete') {
          classes.push('row-grey');
        } else if (row[4] === 0 || row[4] === '0') {
          classes.push('row-grey');
        } else if (row[7] && row[7].toLowerCase() === 'fg ready') {
          classes.push('row-green');
        } else if (row[8] && row[8].toLowerCase() === 'casting available') {
          classes.push('row-purple');
        } else if (row[8] && row[8].toLowerCase() === 'casting short') {
          classes.push('row-red');
        } else if (row[8] && row[8].toLowerCase() === 'machined available') {
          classes.push('row-cyan');
        }
        if (row[11] && row[11].toLowerCase() === 'stranger') {
          classes.push('fg-stranger');
        }
      }
      return classes.join(' ');
    }

    onMounted(() => {
      const saved = localStorage.getItem(FILTERS_KEY);
      if (saved) {
        const filters = JSON.parse(saved);
        oaFilter.value = filters.oaFilter ?? '';
        customerFilter.value = filters.customerFilter ?? '';
        statusFilter.value = filters.statusFilter ?? 'ACTIVE';
      }
      fetchOARegistrar();
      fetchUniqueOAs();
    });

    watch([oaFilter, customerFilter, statusFilter], () => {
      localStorage.setItem(
        FILTERS_KEY,
        JSON.stringify({
          oaFilter: oaFilter.value,
          customerFilter: customerFilter.value,
          statusFilter: statusFilter.value,
        })
      );
    });

    return {
      oaRows, oaList, oaFilter, customerFilter, statusFilter,
      filteredOAList, filteredOANumbers, filteredOARows, getRowClass,
      groupedOARows
    };
  }

  
};
</script>

<style scoped>
/* Fade-in animation for visual cue */
.fade-in {
  animation: fadeIn 0.7s;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(16px);}
  to { opacity: 1; transform: none;}
}

/* Sidebar: dark theme with accent */
.sidebar {
  width: 360px;
  background: linear-gradient(135deg, #232526 0%, #414345 100%);
  color: #f8f8f8;
  padding: 18px 14px;
  border-right: 2px solid #2d2d2d;
  min-height: 100vh;
  box-sizing: border-box;
  position: fixed;
  left: 0;
  font-size: 14px;
  box-shadow: 2px 0 12px rgba(44,62,80,0.10);
  z-index: 2;
}

/* Sidebar filters */
.oa-filters {
  display: flex;
  flex-direction: column;
  gap: 0.7em;
  margin-bottom: 1.2em;
}
.oa-filter-input {
  padding: 0.5em 0.8em;
  border-radius: 6px;
  border: none;
  background: #353b48;
  color: #f8f8f8;
  font-size: 1em;
  outline: none;
  transition: background 0.2s;
}
.oa-filter-input:focus {
  background: #636e72;
}

/* Sidebar table */
.sidebar-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 0.2em;
}
.sidebar-table-container {
  max-height: 60vh; /* Adjust as needed */
  overflow-y: auto;
  margin-bottom: 1em;
}
.sidebar-table th {
  background: #353b48;
  color: #f1c40f;
  font-weight: 600;
  padding: 7px 4px;
  border-radius: 4px 4px 0 0;
  font-size: 13px;
  letter-spacing: 0.5px;
}
.sidebar-table td {
  background: #2d3436;
  color: #f8f8f8;
  padding: 7px 4px;
  border-radius: 0 0 4px 4px;
  font-size: 13px;
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #636e72;
  color: #fff;
  text-transform: capitalize;
}
.status-pill.active { background: #27ae60; }
.status-pill.closed { background: #e74c3c; }
.status-pill.pending { background: #f1c40f; color: #222; }
.status-pill.completed { background: #2980b9; }
.status-pill.inactive { background: #7f8c8d; }

/* Main body: card style */
.body {
  background: #f9fafb;
  margin-left: 360px;
  padding: 32px 28px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 16px rgba(44,62,80,0.08);
  width: calc(100vw - 340px);
  font-size: 15px;
  z-index: 1;
  position: relative;
  transition: background 0.3s;
  overflow-y: auto;
  height: calc(100vh - 0px);
}
.body h2 {
  color: #232526;
  margin-bottom: 18px;
  font-size: 2em;
  font-weight: 700;
  letter-spacing: 1px;
}

/* Main table */
.bom-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(44,62,80,0.06);
}
.bom-table th, .bom-table td {
  padding: 10px 8px;
  text-align: left;
}
.bom-table th {
  background: #f1c40f;
  color: #232526;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.bom-table td {
  color: #232526;
  font-size: 10px;
  border-bottom: 1px solid #f4f4f4;
}
.bom-table tr:last-child td {
  border-bottom: none;
}
.no-data {
  color: #e74c3c;
  font-weight: 600;
  margin-top: 24px;
  font-size: 1.2em;
  text-align: center;
}
.row-grey {
  background-color: #858585 !important;
}
.row-green {
  background-color: #58e65f !important;
}
.row-purple {
  background-color: #b9a1dd !important;
}
.row-red {
  background-color: #f09a90 !important;
}
.row-cyan {
  background-color: #7dd3fc !important;
  color: #0c4a6e !important;
}
.fg-stranger td {
  color: #d61500 !important;
  text-decoration: underline;
}
.row-group-box {
  border-left: 4px solid #222 !important;      /* Black */
  border-right: 4px solid #222 !important;
  box-shadow: 0 0 8px 0 #2222;
  position: relative;
  z-index: 1;
}
.row-group-1.row-group-box {
  border-left-color: #222 !important;       /* Light grey */
  border-right-color: #222 !important;
  box-shadow: 0 0 4px 0 #d3d3d355;
}
.row-group-top {
  border-top: 4px solid #222 !important;
}
.row-group-1.row-group-top {
  border-top-color: #222 !important;
}
.row-group-bottom {
  border-bottom: 4px solid #222 !important;
}
.row-group-1.row-group-bottom {
  border-bottom-color: #222 !important;
}
.date-row {
  font-size: 1.25em;
  font-weight: 700;
  background: #fffbe6 !important;
}
</style>